import { supabase } from "../lib/supabase";

export type RSVPChoice = "yes" | "maybe" | "no";

export interface RSVPData {
  id: string;
  event_id: string;
  user_id: string;
  name: string | null;
  choice: RSVPChoice;
  created_at: string;
  updated_at: string;
}

export interface RSVPCounts {
  yes: number;
  maybe: number;
  no: number;
}

export interface Attendee {
  name: string | null;
  choice: RSVPChoice;
  updated_at: string;
}

export async function upsertRSVP(eventId: string, choice: RSVPChoice, name?: string): Promise<RSVPData> {
  // user_id é default no DB (auth.uid()), não enviar do client
  const { data, error } = await supabase
    .from("rsvps")
    .upsert(
      { event_id: eventId, choice, name: name || null },
      { onConflict: "event_id,user_id" }
    )
    .select()
    .single();
  
  if (error) throw error;
  return data as RSVPData;
}

export async function getUserRSVP(eventId: string): Promise<RSVPData | null> {
  const { data, error } = await supabase
    .from("rsvps")
    .select("*")
    .eq("event_id", eventId)
    .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
    .maybeSingle();
  
  if (error) throw error;
  return data as RSVPData | null;
}

export async function getRSVPCounts(eventId: string): Promise<RSVPCounts> {
  const { data, error } = await supabase.rpc("rsvp_counts", { p_event_id: eventId });
  if (error) throw error;
  
  const base: RSVPCounts = { yes: 0, maybe: 0, no: 0 };
  (data as Array<{ choice: RSVPChoice; total: number }> | null)?.forEach((r) => {
    base[r.choice] = r.total ?? 0;
  });
  
  return base;
}

export async function listAttendees(eventId: string, limit = 5): Promise<Attendee[]> {
  const { data, error } = await supabase
    .from("rsvps")
    .select("name, choice, updated_at")
    .eq("event_id", eventId)
    .eq("choice", "yes")
    .not("name", "is", null)
    .order("updated_at", { ascending: false })
    .limit(limit);
  
  if (error) throw error;
  return (data ?? []) as Attendee[];
}

export function subscribeRSVP(eventId: string, onChange: () => void) {
  const channel = supabase
    .channel(`rsvps:event:${eventId}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "rsvps", filter: `event_id=eq.${eventId}` },
      onChange
    )
    .subscribe();
  
  return () => supabase.removeChannel(channel);
}